import { useState } from "react";
import cn from "classnames";
import { Link } from "react-router-dom";

import {
  useFloating,
  useClick,
  useInteractions,
  autoUpdate,
  shift,
  useRole,
  useDismiss,
  FloatingPortal,
  offset,
  flip,
  FloatingFocusManager,
} from "@floating-ui/react";

// types
import { User } from "store/useAuthStore"

// components
import { Icon } from "@trussworks/react-uswds";
import { RoundedButton } from "components/LibraryComponents/Components";

// store
import { useResponsiveStore } from "store/useResponsiveStore";
import useAuthStore, { ClaimedBusiness } from "store/useAuthStore";

// styles
import styles from "./Menu.module.scss";

export default function Menu({ handleLogout }: { handleLogout: () => void }) {
  const { user } = useAuthStore();

  // user menu
  const [openDesktop, setOpenDesktop] = useState<boolean>(false);

  const { device, mobileMenuOpen } = useResponsiveStore();

  const { refs, floatingStyles, context } = useFloating({
    open: openDesktop,
    placement: "bottom-start",
    onOpenChange: setOpenDesktop,
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip(), shift()],
  });

  const click = useClick(context);
  const role = useRole(context);
  const dismiss = useDismiss(context, { bubbles: true });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    role,
    dismiss,
  ]);

  return (
    <div className={styles["menu-root"]}>
      {/* if on desktop, show user button with floating portal */}
      {device === "desktop" && (
        <>
          <RoundedButton
            variant="ghost"
            className={cn(styles["menu-button"], {
              [styles["open"]]: openDesktop,
            })}
            ref={refs.setReference}
            {...getReferenceProps()}
          >
            {user.name}
            <Icon.ArrowDropDown />
          </RoundedButton>
          <FloatingPortal id="menu-popover">
            <FloatingFocusManager
              context={context}
              modal={false}
              // prevent the first item from auto-focusing on open
              initialFocus={-1}
            >
              {openDesktop && (
                <div
                  ref={refs.setFloating}
                  style={{ ...floatingStyles, outline: "none" }}
                  {...getFloatingProps()}
                  className={styles["menu-content"]}
                >
                  <MenuLinks
                    user={user}
                    handleLogout={handleLogout}
                  />
                </div>
              )}
            </FloatingFocusManager>
          </FloatingPortal>
        </>
      )}

      {/* if mobile menu is open, show user button with simple drop down menu */}
      {mobileMenuOpen && (
        <>
          <p>{user.name}</p>
          <div className={styles["menu-content"]}>
            <MenuLinks
              user={user}
              handleLogout={handleLogout}
            />
          </div>
        </>
      )}
    </div>
  );
}

function MenuLinks({
  user,
  handleLogout,
}: {
  user: User;
  handleLogout: () => void;
}) {

  return (
    <ul className={styles["menu-list"]}>
      {user.role === "user" && (
        <li>
          <Link className={styles["menu-item-a"]} to={"/claim-business"}>
            <div>Claim a business</div>
          </Link>
        </li>
      )}
      {user.role === "admin" && (
        <li>
          <Link className={styles["menu-item-a"]} to={"/sam-entity"}>
            <div>SAM entity lookup</div>
          </Link>
        </li>
      )}

      {user.businesses.map((business) => (
        <li>
          <Link
            className={styles["menu-item-a"]}
            to={`/profile/${business.uei}/${business.cage_codes[0]}/edit`}
          >
            <div>Edit {business.legal_business_name}</div>
          </Link>
        </li>
      ))}

      <li>
        <button className={styles["menu-item-button"]} onClick={handleLogout}>
          Log out
        </button>
      </li>
    </ul>
  );
}